import React, { useEffect, useState } from "react";
import { COLOR } from "./pillars/_cards";

const API = process.env.REACT_APP_BACKEND_URL;

/** Record chemistry + date of a 12V/16V battery replacement (Tesla's API
 *  exposes no service history). Shared by the TSB bulletin card and the
 *  Battery DNA low-voltage card — feeds the record that resets the LV age
 *  model + reminder emails. Calls onChange({replaced_at, chemistry}) after
 *  the initial fetch and on every save so the parent model stays in sync. */
export const LvReplacementForm = ({ vin, onChange, refreshKey = 0, liIonLabel = "Lithium-ion 12V", sixteenV = false }) => {
  const [rec, setRec] = useState(null);
  const [editing, setEditing] = useState(false);
  const [chem, setChem] = useState(sixteenV ? "li-ion" : "agm");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!vin) return;
    fetch(`${API}/api/lv/replaced/${encodeURIComponent(vin)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d?.replaced_at) {
          setRec(d);
          if (d.chemistry) setChem(d.chemistry);
          setDate(d.replaced_at.slice(0, 10));
          onChange?.(d);
        } else if (refreshKey > 0) {
          setRec(null);
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vin, refreshKey]);

  const save = async () => {
    if (!vin || saving) return;
    setSaving(true);
    setError(null);
    try {
      const replaced_at = new Date(`${date}T12:00:00Z`).toISOString();
      const payload = { vin, replaced_at, chemistry: chem };
      if (editing && rec?.replaced_at) payload.prev_replaced_at = rec.replaced_at;
      const r = await fetch(`${API}/api/lv/replaced`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (r.ok) {
        const next = { replaced_at, chemistry: chem };
        setRec(next);
        setEditing(false);
        onChange?.(next);
      } else {
        setError("Couldn't save — please try again");
      }
    } catch (e) {
      setError("Couldn't save — please try again");
    } finally {
      setSaving(false);
    }
  };

  if (!vin) return null;

  if (rec && !editing) {
    return (
      <div className="flex items-center gap-2 mt-1.5" data-testid="tsb-lv-recorded">
        <span className="font-mono text-[9px] uppercase tracking-wider" style={{ color: COLOR.green }}>
          ✓ {rec.chemistry === "li-ion" ? liIonLabel : "Lead-acid (AGM)"} · replaced{" "}
          {new Date(rec.replaced_at).toLocaleDateString()}
        </span>
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="font-mono text-[9px] uppercase tracking-wider underline underline-offset-2 text-zinc-500 hover:text-white transition-colors"
          data-testid="tsb-lv-edit-btn"
        >
          Edit
        </button>
      </div>
    );
  }

  return (
    <div className="mt-2 space-y-2" data-testid="tsb-lv-form">
      <div className="flex items-center gap-1.5">
        {(sixteenV
          ? [["li-ion", liIonLabel]]
          : [["agm", "Lead-acid (AGM)"], ["li-ion", liIonLabel]]
        ).map(([val, label]) => (
          <button
            key={val}
            type="button"
            onClick={() => setChem(val)}
            className="px-2 py-1 rounded-full font-mono text-[9px] uppercase tracking-wider border transition-colors"
            style={chem === val
              ? { color: COLOR.cyan, borderColor: `${COLOR.cyan}66`, background: `${COLOR.cyan}14` }
              : { color: "rgba(255,255,255,0.45)", borderColor: "rgba(255,255,255,0.12)" }}
            data-testid={`tsb-lv-chem-${val}`}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <input
          type="date"
          value={date}
          max={new Date().toISOString().slice(0, 10)}
          onChange={(e) => setDate(e.target.value)}
          className="bg-white/[0.04] border border-white/10 rounded-md px-2 py-1 font-mono text-[10px] text-white [color-scheme:dark]"
          data-testid="tsb-lv-date-input"
        />
        <button
          type="button"
          onClick={save}
          disabled={saving || !date}
          className="px-3 py-1 rounded-full font-mono text-[9px] uppercase tracking-wider font-bold border transition-colors disabled:opacity-50"
          style={{ color: COLOR.green, borderColor: `${COLOR.green}66`, background: `${COLOR.green}14` }}
          data-testid="tsb-lv-save-btn"
        >
          {saving ? "Saving…" : "Save replacement"}
        </button>
      </div>
      {error && (
        <div className="font-mono text-[9px] uppercase tracking-wider" style={{ color: COLOR.red || "#FF5C5C" }} data-testid="tsb-lv-save-error">
          {error}
        </div>
      )}
    </div>
  );
};
